"use client";

import { ArrowRight, Play, Truck, ShieldCheck, BadgeCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheCommandCenter() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const microTags = [{
    id: 1,
    icon: Truck,
    label: "Weekly Workshops"
  }, {
    id: 2,
    icon: BadgeCheck,
    label: "Discord Community"
  }, {
    id: 3,
    icon: ShieldCheck,
    label: "Project Nights"
  }];
  const specPills = [{
    id: 1,
    label: "Open Source",
    position: "top-[12%] left-[-4%]",
    delay: "0.4s"
  }, {
    id: 2,
    label: "Hardware",
    position: "top-[38%] right-[-6%]",
    delay: "0.6s"
  }, {
    id: 3,
    label: "Hackathons",
    position: "bottom-[18%] left-[2%]",
    delay: "0.8s"
  }, {
    id: 4,
    label: "Networking",
    position: "bottom-[34%] right-[-2%]",
    delay: "1s"
  }];
  const marqueeItems = ["Weekly coding workshops", "Collaborative project nights", "Open-source contributions", "Hardware hacking sessions", "Weekly coding workshops", "Collaborative project nights", "Open-source contributions", "Hardware hacking sessions"];
  return <section data-section-id="137" id="the-command-center" className="bg-background text-foreground relative overflow-hidden min-h-[92vh] flex flex-col">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[-5%] w-[500px] h-[500px] rounded-lg blur-[120px] opacity-50" style={{
        background: "radial-gradient(circle, var(--chart-1) 0%, transparent 70%)",
        animation: "drift1 18s ease-in-out infinite"
      }} />
        <div className="absolute top-[30%] right-[-10%] w-[600px] h-[600px] rounded-lg blur-[140px] opacity-40" style={{
        background: "radial-gradient(circle, var(--chart-2) 0%, transparent 70%)",
        animation: "drift2 20s ease-in-out infinite"
      }} />
        <div className="absolute bottom-[-15%] left-[20%] w-[550px] h-[550px] rounded-lg blur-[130px] opacity-35" style={{
        background: "radial-gradient(circle, var(--chart-3) 0%, transparent 70%)",
        animation: "drift3 19s ease-in-out infinite"
      }} />
      </div>

      {}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "60px 60px"
    }} />

      <div className="relative flex-1 flex items-center px-6 md:px-12 lg:px-16 py-16 lg:py-20">
        <div className="w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-7 flex flex-col gap-8 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            {}
            <div className="flex items-center gap-3">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-lg opacity-75 animate-ping" style={{
                backgroundColor: "var(--chart-4)"
              }} />
                <span className="relative inline-flex rounded-lg h-2 w-2" style={{
                backgroundColor: "var(--chart-4)"
              }} />
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">&nbsp;COMMUNITY TECH HUB</span>
            </div>

            {}
            <h1 className="font-default font-semibold leading-[0.95] text-foreground" style={{
            fontSize: "clamp(2.75rem, 8vw, 7.5rem)",
            letterSpacing: "-0.04em"
          }}>
              The{" "}
              <span className="inline-block bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(135deg, var(--chart-1) 0%, var(--chart-2) 50%, var(--chart-3) 100%)"
            }}>
                command
              </span>
              <br />
              center for builders.
            </h1>

            {}
            <p className="text-base md:text-lg text-muted-foreground max-w-[480px] leading-relaxed">
              Code Hub is your local community for technology and coding. Join us for hackathons, passion projects, and hardware building nights in a collaborative environment.
            </p>

            {}
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="h-14 px-8 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg text-base font-medium group">
                <Link to="/JoinUsPage">
                  Download Toolkit
                  <ArrowRight className="size-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button asChild variant="ghost" size="lg" className="h-14 px-6 rounded-lg border border-border bg-background/40 backdrop-blur-sm hover:bg-secondary text-foreground text-base font-medium group">
                <Link to="/CommunityPage">
                  <Play className="size-4 fill-current" />
                  Explore Projects
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>

            {}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-3 pt-2">
              {microTags.map((tag, i) => {
              const Icon = tag.icon;
              return <div key={tag.id} data-index={i} className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Icon className="size-3.5 text-muted-foreground" />
                      <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                        {tag.label}
                      </span>
                    </div>
                    {i < microTags.length - 1 && <span className="hidden sm:inline w-1 h-1 rounded-lg bg-muted-foreground/40" />}
                  </div>;
            })}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-5 relative flex items-center justify-center h-[400px] md:h-[500px] lg:h-[600px] transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            {}
            <div className="absolute w-[450px] h-[450px] rounded-lg blur-[80px] opacity-60" style={{
            background: "radial-gradient(circle, var(--chart-1) 0%, transparent 65%)"
          }} />

            {}
            <svg className="absolute w-[480px] h-[480px] opacity-25" viewBox="0 0 480 480" fill="none" style={{
            animation: "spin-slow 40s linear infinite"
          }}>
              <defs>
                <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="var(--chart-1)" />
                  <stop offset="50%" stopColor="var(--chart-2)" />
                  <stop offset="100%" stopColor="var(--chart-3)" />
                </linearGradient>
              </defs>
              <polygon points="240,20 280,80 240,140 280,200 240,260 280,320 240,380 280,440 240,460 200,440 240,380 200,320 240,260 200,200 240,140 200,80 240,20" stroke="url(#ringGrad)" strokeWidth="1.5" fill="none" />
              <circle cx="240" cy="240" r="220" stroke="url(#ringGrad)" strokeWidth="1" strokeDasharray="4 8" fill="none" />
              <circle cx="240" cy="240" r="180" stroke="url(#ringGrad)" strokeWidth="1" strokeDasharray="2 12" fill="none" />
            </svg>

            {}
            <div className="relative z-10" style={{
            transform: "rotate(15deg)",
            animation: "float 6s ease-in-out infinite"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_working_at_hub.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_working_at_hub.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701217/img/member_working_at_hub.png-wvc-sizes" alt="Code Hub member working on a project" loading="lazy" decoding="async" className="w-[240px] md:w-[300px] lg:w-[340px] h-auto rounded-lg shadow-2xl" style={{
              filter: "drop-shadow(0 30px 60px hsla(270, 100%, 64%, 0.4))"
            }} />
              {}
              <div className="absolute top-[15%] right-[10%] w-16 h-16 rounded-lg blur-xl opacity-70 pointer-events-none" style={{
              background: "radial-gradient(circle, hsl(0 0% 100%) 0%, transparent 70%)"
            }} />
            </div>

            {}
            {specPills.map((pill, i) => <div key={pill.id} data-index={i} className={`absolute ${pill.position} z-20`} style={{
            animation: `fadeInPill 0.8s ease-out ${pill.delay} both, float 6s ease-in-out infinite ${pill.delay}`
          }}>
                <Badge variant="outline" className="bg-background/60 backdrop-blur-md border-border/60 px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-foreground shadow-lg">
                  <span className="w-1.5 h-1.5 rounded-lg mr-1" style={{
                backgroundColor: "var(--chart-1)"
              }} />
                  {pill.label}
                </Badge>
              </div>)}
          </div>
        </div>
      </div>

      {}
      <div className="relative border-t border-border/50 bg-background/40 backdrop-blur-sm py-3 overflow-hidden">
        <div className="flex gap-12 whitespace-nowrap" style={{
        animation: "marquee 40s linear infinite"
      }}>
          {[...marqueeItems, ...marqueeItems].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-12 shrink-0">
              <span className="font-mono text-[13px] uppercase tracking-wider text-muted-foreground">
                {item}
              </span>
              <span className="w-1 h-1 rounded-lg shrink-0" style={{
            backgroundColor: "var(--chart-1)"
          }} />
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes drift1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(40px, 30px) scale(1.1); }
        }
        @keyframes drift2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-50px, 40px) scale(1.15); }
        }
        @keyframes drift3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(30px, -40px) scale(1.08); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: rotate(15deg) translateY(0); }
          50% { transform: rotate(15deg) translateY(-12px); }
        }
        @keyframes fadeInPill {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}